import { useEffect, useRef, useState } from "react";
import { coachApi, api, type AthleteDigest } from "@/api";
import type { CoachRosterEntry } from "@/lib/types";
import { useUI } from "@/lib/theme";

const FLAG_COLORS: Record<string, string> = {
  alert: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/20",
  warn: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20",
  info: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/20",
};

function FlagBadge({
  code,
  severity,
  label,
  description,
}: {
  code: string;
  severity: string;
  label: string;
  description: string;
}) {
  const cls = FLAG_COLORS[severity] ?? FLAG_COLORS["info"]!;
  const ref = useRef<HTMLSpanElement>(null);
  const [show, setShow] = useState(false);
  return (
    <span className="relative">
      <span
        ref={ref}
        className={`cursor-default rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        tabIndex={0}
        role="status"
        aria-label={`${label}: ${description}`}
      >
        {label}
      </span>
      {show && (
        <span className="absolute bottom-full left-0 z-50 mb-1.5 w-52 rounded-lg border border-line bg-panel px-3 py-2 text-xs leading-snug text-muted shadow-md">
          {description}
        </span>
      )}
    </span>
  );
}

function tsbBadge(tsb: number) {
  const cls =
    tsb > 5
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : tsb > -10
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
        : "bg-rose-500/15 text-rose-600 dark:text-rose-400";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${cls}`}>
      TSB {tsb > 0 ? "+" : ""}
      {Math.round(tsb)}
    </span>
  );
}

function Avatar({ entry }: { entry: CoachRosterEntry }) {
  const name = [entry.firstname, entry.lastname].filter(Boolean).join(" ") || "?";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (entry.profilePic) {
    return (
      <img
        src={entry.profilePic}
        alt={name}
        className="h-12 w-12 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/20 text-base font-semibold text-accent">
      {initials}
    </div>
  );
}

export default function CoachRoster({
  onSelect,
  onMessage,
  onCalendar,
}: {
  onSelect: (id: string, name: string) => void;
  onMessage?: (id: string, name: string) => void;
  onCalendar?: (id: string, name: string) => void;
}) {
  const { t, lang } = useUI();
  const [athletes, setAthletes] = useState<CoachRosterEntry[] | null>(null);
  const [error, setError] = useState(false);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [digests, setDigests] = useState<Record<string, AthleteDigest | "loading">>({});

  const handleDigest = (athleteId: string, refresh = false) => {
    setDigests((prev) => ({ ...prev, [athleteId]: "loading" }));
    api
      .athleteDigest(athleteId, lang as "es" | "en", refresh)
      .then((d) => setDigests((prev) => ({ ...prev, [athleteId]: d })))
      .catch(() => setDigests((prev) => { const n = { ...prev }; delete n[athleteId]; return n; }));
  };

  const load = () => {
    coachApi
      .athletes()
      .then((r) => setAthletes(r.athletes))
      .catch(() => setError(true));
  };

  useEffect(() => { load(); }, []);

  const handleUnlink = async (a: CoachRosterEntry) => {
    const name = [a.firstname, a.lastname].filter(Boolean).join(" ") || a.userId;
    const msg =
      lang === "es"
        ? `¿Desvincular a ${name}? El vínculo se eliminará de inmediato.`
        : `Unlink ${name}? The link will be removed immediately.`;
    if (!confirm(msg)) return;
    setUnlinking(a.userId);
    try {
      await coachApi.revokeAthlete(a.userId);
      setAthletes((prev) => prev?.filter((x) => x.userId !== a.userId) ?? null);
    } finally {
      setUnlinking(null);
    }
  };

  if (error) {
    return (
      <div className="py-12 text-center text-sm text-muted">
        {lang === "es" ? "Error al cargar los atletas." : "Error loading athletes."}
      </div>
    );
  }

  if (!athletes) {
    return (
      <div className="py-12 text-center text-sm text-muted">{t("admin_loading")}</div>
    );
  }

  if (athletes.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-lg font-semibold">{t("coach_roster_empty")}</p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
          {t("coach_roster_empty_hint")}
        </p>
      </div>
    );
  }

  const sorted = [...athletes].sort((a, b) => {
    const haAlert = (x: CoachRosterEntry) => x.flags.some((f) => f.severity === "alert") ? 0 : 1;
    return haAlert(a) - haAlert(b);
  });

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((a) => {
          const name = [a.firstname, a.lastname].filter(Boolean).join(" ") || a.userId;
          return (
            <div
              key={a.userId}
              className="flex flex-col gap-3 rounded-2xl border border-line bg-panel2/40 p-4 transition hover:border-accent/30"
            >
              {/* Identidad */}
              <div className="flex items-center gap-3">
                <Avatar entry={a} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-fg">{name}</p>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {a.hasShareableData ? (
                      <>
                        {tsbBadge(a.tsb)}
                        {a.readiness != null && (
                          <span className="rounded-full bg-panel px-2 py-0.5 text-xs text-muted">
                            ❤ {a.readiness}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                        {t("coach_roster_no_shareable")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Métricas compactas */}
              <div className="grid grid-cols-3 gap-2 rounded-xl bg-panel px-3 py-2 text-center text-xs">
                <div>
                  <p className="font-display font-semibold tabular-nums text-fg">
                    {a.sessionsThisWeek}
                  </p>
                  <p className="text-muted">{lang === "es" ? "Sesiones" : "Sessions"}</p>
                </div>
                <div>
                  <p className="font-display font-semibold tabular-nums text-fg">
                    {a.sleepScore != null ? a.sleepScore : "—"}
                  </p>
                  <p className="text-muted">{lang === "es" ? "Sueño" : "Sleep"}</p>
                </div>
                <div>
                  <p className="font-display font-semibold tabular-nums text-fg">
                    {a.bbHigh != null ? Math.round(a.bbHigh) : "—"}
                  </p>
                  <p className="text-muted">BB</p>
                </div>
              </div>

              {/* Flags */}
              {a.flags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {a.flags.map((f) => (
                    <FlagBadge
                      key={f.code}
                      code={f.code}
                      severity={f.severity}
                      label={t(`flag_${f.code}` as Parameters<typeof t>[0])}
                      description={t(`flag_${f.code}_desc` as Parameters<typeof t>[0])}
                    />
                  ))}
                </div>
              )}

              {/* Digest IA */}
              {digests[a.userId] === "loading" ? (
                <p className="text-xs text-muted italic">{lang === "es" ? "Generando resumen…" : "Generating summary…"}</p>
              ) : digests[a.userId] ? (
                <div className="rounded-lg border border-line bg-bg p-2 text-xs text-fg">
                  <p className="mb-1">{(digests[a.userId] as AthleteDigest).text}</p>
                  <div className="flex items-center gap-2 text-muted/70">
                    <span>{(digests[a.userId] as AthleteDigest).source === "ai" ? "✦ IA" : "⚙ reglas"}</span>
                    {(digests[a.userId] as AthleteDigest).cached && <span>· {lang === "es" ? "cacheado" : "cached"}</span>}
                    <button
                      onClick={() => handleDigest(a.userId, true)}
                      className="ml-auto underline underline-offset-2 hover:text-fg"
                    >
                      {lang === "es" ? "Regenerar" : "Refresh"}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Acciones */}
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => onSelect(a.userId, name)}
                  className="flex-1 rounded-lg bg-accent/10 px-3 py-1.5 text-xs font-semibold text-accent transition hover:bg-accent/20"
                >
                  {lang === "es" ? "Ver perfil" : "View profile"}
                </button>
                {onMessage && (
                  <button
                    onClick={() => onMessage(a.userId, name)}
                    className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-muted transition hover:text-fg"
                  >
                    {t("msg_tab")}
                  </button>
                )}
                {onCalendar && (
                  <button
                    onClick={() => onCalendar(a.userId, name)}
                    className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-muted transition hover:text-fg"
                  >
                    {t("cal_plan_tab")}
                  </button>
                )}
                <button
                  onClick={() => handleDigest(a.userId)}
                  disabled={digests[a.userId] === "loading"}
                  className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-muted transition hover:text-fg disabled:opacity-40"
                  title={lang === "es" ? "Resumen IA del estado" : "AI status digest"}
                >
                  ✦ {lang === "es" ? "Resumen" : "Digest"}
                </button>
                <button
                  onClick={() => void handleUnlink(a)}
                  disabled={unlinking === a.userId}
                  className="rounded-lg border border-rose-500/30 px-3 py-1.5 text-xs font-medium text-rose-500 transition hover:bg-rose-500/10 disabled:opacity-40"
                >
                  {lang === "es" ? "Desvincular" : "Unlink"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
